import type { Component } from '../router';
import type { MsgKey } from '../i18n/messages';
import { t } from '../i18n/locale';

class MarketingHome implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const wrap = document.createElement('div');
    wrap.className = 'home';

    wrap.appendChild(this.renderHero());
    wrap.appendChild(this.renderHighlights());
    wrap.appendChild(this.renderWho());
    wrap.appendChild(this.renderWhat());
    wrap.appendChild(this.renderWhere());
    wrap.appendChild(this.renderSupply());
    wrap.appendChild(this.renderPartners());
    wrap.appendChild(this.renderIndustries());
    wrap.appendChild(this.renderLogistics());
    wrap.appendChild(this.renderWhy());
    wrap.appendChild(this.renderBuyers());
    wrap.appendChild(this.renderInfo());
    wrap.appendChild(this.renderClosing());

    host.appendChild(wrap);
    this.root = wrap;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  // ── Hero ──────────────────────────────────────────────────────────

  private renderHero(): HTMLElement {
    const hero = document.createElement('section');
    hero.className = 'home__hero';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'home__eyebrow';
    eyebrow.textContent = t('home.eyebrow');

    const lead = document.createElement('h1');
    lead.className = 'home__lead';
    lead.textContent = t('home.lead');

    const sub = document.createElement('p');
    sub.className = 'home__sub';
    sub.textContent = t('home.sub');

    const ctas = document.createElement('div');
    ctas.className = 'home__ctas';

    const cta1 = document.createElement('a');
    cta1.className = 'home__cta';
    cta1.href = '/contact';
    cta1.textContent = t('home.cta');

    const cta2 = document.createElement('a');
    cta2.className = 'home__cta home__cta--secondary';
    cta2.href = '/contact';
    cta2.textContent = t('home.cta2');

    ctas.append(cta1, cta2);
    hero.append(eyebrow, lead, sub, ctas);
    return hero;
  }

  // ── Highlight cards ───────────────────────────────────────────────

  private renderHighlights(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'home__highlights';

    const items: Array<{ title: MsgKey; body: MsgKey }> = [
      { title: 'home.card1.title', body: 'home.card1.body' },
      { title: 'home.card2.title', body: 'home.card2.body' },
      { title: 'home.card3.title', body: 'home.card3.body' },
    ];

    for (const { title, body } of items) {
      const card = document.createElement('article');
      card.className = 'home-card';
      const h = document.createElement('h2');
      h.className = 'home-card__title';
      h.textContent = t(title);
      const p = document.createElement('p');
      p.className = 'home-card__body';
      p.textContent = t(body);
      card.append(h, p);
      section.appendChild(card);
    }
    return section;
  }

  // ── Who We Are ────────────────────────────────────────────────────

  private renderWho(): HTMLElement {
    const section = this.section('about', t('who.title'));
    const body = document.createElement('div');
    body.className = 'home-section__prose';
    for (const key of ['who.p1', 'who.p2', 'who.p3'] as MsgKey[]) {
      const p = document.createElement('p');
      p.textContent = t(key);
      body.appendChild(p);
    }
    section.appendChild(body);
    return section;
  }

  // ── What We Do ────────────────────────────────────────────────────

  private renderWhat(): HTMLElement {
    const section = this.section('what-we-do', t('what.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('what.intro');
    section.appendChild(intro);

    const grid = document.createElement('div');
    grid.className = 'home-grid home-grid--3';
    const items: Array<{ title: MsgKey; body: MsgKey }> = [
      { title: 'what.sourcing.title', body: 'what.sourcing.body' },
      { title: 'what.logistics.title', body: 'what.logistics.body' },
      { title: 'what.expertise.title', body: 'what.expertise.body' },
    ];
    for (const { title, body } of items) {
      grid.appendChild(this.card(t(title), t(body)));
    }
    section.appendChild(grid);
    return section;
  }

  // ── Where We Operate ──────────────────────────────────────────────

  private renderWhere(): HTMLElement {
    const section = this.section('where', t('where.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('where.intro');
    section.appendChild(intro);

    const grid = document.createElement('div');
    grid.className = 'home-grid home-grid--3';
    const regions: Array<{ name: MsgKey; desc: MsgKey }> = [
      { name: 'where.us.name', desc: 'where.us.desc' },
      { name: 'where.mx.name', desc: 'where.mx.desc' },
      { name: 'where.sa.name', desc: 'where.sa.desc' },
    ];
    for (const { name, desc } of regions) {
      grid.appendChild(this.card(t(name), t(desc)));
    }
    section.appendChild(grid);

    const outro = document.createElement('p');
    outro.className = 'home-section__outro';
    outro.textContent = t('where.outro');
    section.appendChild(outro);
    return section;
  }

  // ── What We Supply ────────────────────────────────────────────────

  private renderSupply(): HTMLElement {
    const section = this.section('supply', t('supply.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('supply.intro');
    section.appendChild(intro);

    const categories: Array<{
      title: MsgKey;
      items: Array<{ name: MsgKey; desc: MsgKey }>;
    }> = [
      {
        title: 'supply.core.title',
        items: [
          { name: 'supply.core.btx.name', desc: 'supply.core.btx.desc' },
          { name: 'supply.core.olefins.name', desc: 'supply.core.olefins.desc' },
          { name: 'supply.core.solvents.name', desc: 'supply.core.solvents.desc' },
          { name: 'supply.core.fuel.name', desc: 'supply.core.fuel.desc' },
        ],
      },
      {
        title: 'supply.refining.title',
        items: [
          { name: 'supply.refining.catalysts.name', desc: 'supply.refining.catalysts.desc' },
          { name: 'supply.refining.hydro.name', desc: 'supply.refining.hydro.desc' },
          { name: 'supply.refining.process.name', desc: 'supply.refining.process.desc' },
        ],
      },
      {
        title: 'supply.specialty.title',
        items: [
          { name: 'supply.specialty.additives.name', desc: 'supply.specialty.additives.desc' },
          { name: 'supply.specialty.coatings.name', desc: 'supply.specialty.coatings.desc' },
          { name: 'supply.specialty.custom.name', desc: 'supply.specialty.custom.desc' },
        ],
      },
    ];

    for (const cat of categories) {
      const group = document.createElement('div');
      group.className = 'home-supply-group';
      const h3 = document.createElement('h3');
      h3.className = 'home-supply-group__title';
      h3.textContent = t(cat.title);
      group.appendChild(h3);
      const list = document.createElement('dl');
      list.className = 'home-supply-list';
      for (const { name, desc } of cat.items) {
        const dt = document.createElement('dt');
        dt.textContent = t(name);
        const dd = document.createElement('dd');
        dd.textContent = t(desc);
        list.append(dt, dd);
      }
      group.appendChild(list);
      section.appendChild(group);
    }

    const outro = document.createElement('p');
    outro.className = 'home-section__outro';
    outro.textContent = t('supply.outro');
    section.appendChild(outro);
    return section;
  }

  // ── Partners ──────────────────────────────────────────────────────

  private renderPartners(): HTMLElement {
    const section = this.section('partners', t('partners.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('partners.intro');
    section.appendChild(intro);

    const list = document.createElement('ul');
    list.className = 'home-bullet-list';
    for (const key of [
      'partners.basf',
      'partners.3m',
      'partners.honeywell',
      'partners.marcopolo',
    ] as MsgKey[]) {
      const li = document.createElement('li');
      li.textContent = t(key);
      list.appendChild(li);
    }
    section.appendChild(list);

    const outro = document.createElement('p');
    outro.className = 'home-section__outro';
    outro.textContent = t('partners.outro');
    section.appendChild(outro);
    return section;
  }

  // ── Industries ────────────────────────────────────────────────────

  private renderIndustries(): HTMLElement {
    const section = this.section('industries', t('industries.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('industries.intro');
    section.appendChild(intro);

    const list = document.createElement('ul');
    list.className = 'home-bullet-list';
    for (const key of [
      'industries.oilgas',
      'industries.refiners',
      'industries.manufacturers',
      'industries.energy',
      'industries.government',
    ] as MsgKey[]) {
      const li = document.createElement('li');
      li.textContent = t(key);
      list.appendChild(li);
    }
    section.appendChild(list);

    const outro = document.createElement('p');
    outro.className = 'home-section__outro home-section__outro--strong';
    outro.textContent = t('industries.outro');
    section.appendChild(outro);
    return section;
  }

  // ── Logistics ─────────────────────────────────────────────────────

  private renderLogistics(): HTMLElement {
    const section = this.section('logistics', t('logistics.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('logistics.intro');
    section.appendChild(intro);

    const grid = document.createElement('div');
    grid.className = 'home-grid home-grid--2';
    const items: Array<{ title: MsgKey; desc: MsgKey }> = [
      { title: 'logistics.formats.title', desc: 'logistics.formats.desc' },
      { title: 'logistics.terminal.title', desc: 'logistics.terminal.desc' },
      { title: 'logistics.compliance.title', desc: 'logistics.compliance.desc' },
      { title: 'logistics.terms.title', desc: 'logistics.terms.desc' },
      { title: 'logistics.rapid.title', desc: 'logistics.rapid.desc' },
    ];
    for (const { title, desc } of items) {
      grid.appendChild(this.card(t(title), t(desc)));
    }
    section.appendChild(grid);

    const outro = document.createElement('p');
    outro.className = 'home-section__outro';
    outro.textContent = t('logistics.outro');
    section.appendChild(outro);

    const note = document.createElement('aside');
    note.className = 'home-trade-note';
    note.textContent = t('logistics.note');
    section.appendChild(note);
    return section;
  }

  // ── Why BFC ───────────────────────────────────────────────────────

  private renderWhy(): HTMLElement {
    const section = this.section('why', t('why.title'));
    const grid = document.createElement('div');
    grid.className = 'home-grid home-grid--1';
    const items: Array<{ title: MsgKey; body: MsgKey }> = [
      { title: 'why.supply.title', body: 'why.supply.body' },
      { title: 'why.speed.title', body: 'why.speed.body' },
      { title: 'why.rules.title', body: 'why.rules.body' },
      { title: 'why.practical.title', body: 'why.practical.body' },
      { title: 'why.protect.title', body: 'why.protect.body' },
    ];
    for (const { title, body } of items) {
      const row = document.createElement('div');
      row.className = 'home-why-row';
      const h3 = document.createElement('h3');
      h3.className = 'home-why-row__title';
      h3.textContent = t(title);
      const p = document.createElement('p');
      p.className = 'home-why-row__body';
      p.textContent = t(body);
      row.append(h3, p);
      grid.appendChild(row);
    }
    section.appendChild(grid);
    return section;
  }

  // ── For Buyers ────────────────────────────────────────────────────

  private renderBuyers(): HTMLElement {
    const section = this.section('buyers', t('buyers.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('buyers.intro');
    section.appendChild(intro);

    const list = document.createElement('ul');
    list.className = 'home-bullet-list';
    for (const key of [
      'buyers.pain1',
      'buyers.pain2',
      'buyers.pain3',
      'buyers.pain4',
    ] as MsgKey[]) {
      const li = document.createElement('li');
      li.textContent = t(key);
      list.appendChild(li);
    }
    section.appendChild(list);

    const outro = document.createElement('p');
    outro.className = 'home-section__outro';
    outro.textContent = t('buyers.outro');
    section.appendChild(outro);

    const cta = document.createElement('a');
    cta.className = 'home__cta';
    cta.href = '/contact';
    cta.textContent = t('buyers.cta');
    section.appendChild(cta);
    return section;
  }

  // ── Contact info ──────────────────────────────────────────────────

  private renderInfo(): HTMLElement {
    const section = this.section('contact-info', t('info.title'));
    const intro = document.createElement('p');
    intro.className = 'home-section__intro';
    intro.textContent = t('info.intro');
    section.appendChild(intro);

    const list = document.createElement('dl');
    list.className = 'home-contact-list';

    const channels: Array<{ label: MsgKey; value: string }> = [
      { label: 'info.sales', value: 'sales@bluefieldchem.com' },
      { label: 'info.procurement', value: 'procurement@bluefieldchem.com' },
      { label: 'info.trading', value: 'trading@bluefieldchem.com' },
    ];
    for (const { label, value } of channels) {
      const dt = document.createElement('dt');
      dt.textContent = t(label);
      const dd = document.createElement('dd');
      const a = document.createElement('a');
      a.href = `mailto:${value}`;
      a.textContent = value;
      a.className = 'home-contact-list__link';
      dd.appendChild(a);
      list.append(dt, dd);
    }

    // Phone — TODO placeholder
    const dtPhone = document.createElement('dt');
    dtPhone.textContent = t('info.phone');
    const ddPhone = document.createElement('dd');
    ddPhone.className = 'home-contact-list__placeholder';
    ddPhone.textContent = '—';
    list.append(dtPhone, ddPhone);

    // Offices — TODO placeholder
    const dtOffices = document.createElement('dt');
    dtOffices.textContent = t('info.offices');
    const ddOffices = document.createElement('dd');
    ddOffices.className = 'home-contact-list__placeholder';
    ddOffices.textContent = 'United States · Mexico · South America';
    list.append(dtOffices, ddOffices);

    section.appendChild(list);
    return section;
  }

  // ── Closing ───────────────────────────────────────────────────────

  private renderClosing(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'home-section home-closing';
    const p = document.createElement('p');
    p.className = 'home-closing__text';
    p.textContent = t('closing.statement');
    section.appendChild(p);
    const privacy = document.createElement('a');
    privacy.className = 'home-closing__privacy';
    privacy.href = '/privacy';
    privacy.textContent = t('footer.privacy');
    section.appendChild(privacy);
    return section;
  }

  // ── Helpers ───────────────────────────────────────────────────────

  private section(id: string, title: string): HTMLElement {
    const section = document.createElement('section');
    section.className = 'home-section';
    section.id = id;
    const h2 = document.createElement('h2');
    h2.className = 'home-section__title';
    h2.textContent = title;
    section.appendChild(h2);
    return section;
  }

  private card(title: string, body: string): HTMLElement {
    const card = document.createElement('article');
    card.className = 'home-card';
    const h = document.createElement('h3');
    h.className = 'home-card__title';
    h.textContent = title;
    const p = document.createElement('p');
    p.className = 'home-card__body';
    p.textContent = body;
    card.append(h, p);
    return card;
  }
}

export function HomePage(): Component {
  return new MarketingHome();
}
